import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Card, Button, Toggle, dotColor } from "@/components/primitives";

function Slider({ value, min, max, step, severity, onChange }) {
  const color = dotColor(severity);
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div style={{ position: "relative", height: 22, display: "flex", alignItems: "center" }}>
      <div style={{ position: "absolute", left: 0, right: 0, height: 6, borderRadius: 4, background: "var(--ui-track)" }} />
      <div style={{ position: "absolute", left: 0, width: `${pct}%`, height: 6, borderRadius: 4, background: color }} />
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ position: "absolute", left: 0, right: 0, width: "100%", margin: 0, height: 22, cursor: "pointer", opacity: 0 }} />
      <div style={{ position: "absolute", left: `calc(${pct}% - 8px)`, width: 16, height: 16, borderRadius: 9999,
        background: "var(--ui-on-accent)", border: `3px solid ${color}`, boxShadow: "0 2px 6px rgba(0,0,0,.4)", pointerEvents: "none" }} />
    </div>
  );
}

export default function Thresholds() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: endpoints.settings });
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);
  const s4Available = data?.s4Available;

  useEffect(() => { if (data?.settings) setS(JSON.parse(JSON.stringify(data.settings))); }, [data]);
  if (!s) return <div className="prose-i" style={{ color: "var(--ui-text-lo)" }}>Loading…</div>;

  const updRule = (key, patch) => setS((p) => ({ ...p, thresholds: { ...p.thresholds, [key]: { ...p.thresholds[key], ...patch } } }));
  const save = async () => { await endpoints.saveSettings(s); setSaved(true); setTimeout(() => setSaved(false), 1800); await qc.invalidateQueries(); };
  const reset = async () => { await endpoints.resetSettings(); const fresh = await endpoints.settings(); setS(JSON.parse(JSON.stringify(fresh.settings))); await qc.invalidateQueries(); };

  const RULES = ["s1", "s2", "s3", "s4", "s5"];
  const inputStyle = { background: "var(--ui-bg)", border: "1px solid var(--ui-line)", borderRadius: 8, padding: "7px 12px", color: "var(--ui-text-hi)", fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, width: 90, textAlign: "right" };

  return (
    <div>
      <div className="flex" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div className="num" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.9px", color: "var(--ui-text-hi)" }}>Thresholds</div>
          <div className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-lo)", marginTop: 6 }}>You decide what counts as a leak. Nothing fires without your rules.</div>
        </div>
        <div className="flex" style={{ gap: 12 }}>
          <Button variant="secondary" data-testid="reset-defaults" onClick={reset}>Reset to defaults</Button>
          <Button data-testid="save-changes" onClick={save}>{saved ? "Saved ✓" : "Save changes"}</Button>
        </div>
      </div>

      <div className="flex" style={{ gap: 20, alignItems: "flex-start" }}>
        {/* detection rules */}
        <Card style={{ flex: 1, padding: 26 }}>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Detection rules</div>
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 16 }}>turn signals on or off, set sensitivity</div>
          {RULES.map((k, i) => {
            const r = s.thresholds?.[k];
            if (!r) return null;   // defensive: never crash on a missing rule
            const isS4 = k === "s4";
            const disabled = !r.enabled || (isS4 && !s4Available);
            const sev = r.severity || "watch";
            const rmin = r.min ?? 0, rmax = r.max ?? 100, rstep = r.step ?? 1;
            const rval = r.value ?? rmin;
            return (
              <div key={k} style={{ padding: "18px 0 20px", borderTop: i === 0 ? "none" : "1px solid var(--ui-line-soft)", opacity: disabled ? 0.55 : 1 }}>
                <div className="flex" style={{ gap: 12, alignItems: "flex-start" }}>
                  <span style={{ width: 3, alignSelf: "stretch", background: disabled ? "var(--ui-line)" : dotColor(sev), borderRadius: 4, minHeight: 34 }} />
                  <div style={{ flex: 1 }}>
                    <div className="prose-i" style={{ fontSize: 15, fontWeight: 600, color: disabled ? "var(--ui-text-lo)" : "var(--ui-text-hi)" }}>{r.name}</div>
                    <div className="prose-i" style={{ fontSize: 12.5, color: "var(--ui-text-lo)", marginTop: 3 }}>
                      {isS4 && !s4Available ? "Unavailable — no committed volumes recorded in Simpro" : r.description}
                    </div>
                  </div>
                  <span className="num" style={{ fontSize: 13, color: "var(--ui-text-mid)", background: "var(--ui-card-2)", border: "1px solid var(--ui-line)", borderRadius: 8, padding: "6px 12px" }}>
                    {rval}{r.unit === "x" ? "×" : r.unit === "%" ? "%" : ` ${r.unit || ""}`}
                  </span>
                  <Toggle on={r.enabled && !(isS4 && !s4Available)} disabled={isS4 && !s4Available}
                    severity={sev} onChange={(v) => updRule(k, { enabled: v })} />
                </div>
                {r.enabled && !(isS4 && !s4Available) && (
                  <div style={{ marginTop: 14, marginLeft: 15 }}>
                    <Slider value={rval} min={rmin} max={rmax} step={rstep} severity={sev}
                      onChange={(v) => updRule(k, { value: v })} />
                  </div>
                )}
              </div>
            );
          })}
        </Card>

        {/* right column */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 20 }}>
          <Card style={{ padding: 26 }}>
            <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Value assumptions</div>
            <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 12 }}>used to price the exposure</div>
            {[["Margin (% of sell price)", "marginPct", "%"], ["Annualisation factor", "annualisationFactor", "x"], ["Minimum exposure to flag", "minimumExposure", "$"]].map(([label, key, pre], i) => (
              <div key={key} className="flex items-center" data-testid={`setting-${key}`} style={{ justifyContent: "space-between", padding: "16px 0", borderTop: i === 0 ? "none" : "1px solid var(--ui-line-soft)" }}>
                <span className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-hi)" }}>{label}</span>
                <div className="flex items-center" style={{ gap: 6 }}>
                  {pre === "$" && <span className="num" style={{ color: "var(--ui-text-lo)" }}>$</span>}
                  <input type="number" value={s[key] ?? (key === "marginPct" ? 50 : "")} onChange={(e) => setS((p) => ({ ...p, [key]: parseFloat(e.target.value) }))} style={inputStyle} />
                  {pre === "%" && <span className="num" style={{ color: "var(--ui-text-lo)" }}>%</span>}
                  {pre === "x" && <span className="num" style={{ color: "var(--ui-text-lo)" }}>×</span>}
                </div>
              </div>
            ))}
            <div className="flex items-center" style={{ justifyContent: "space-between", padding: "16px 0", borderTop: "1px solid var(--ui-line-soft)" }}>
              <div>
                <span className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-hi)" }}>Realised sell price / kg</span>
                <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)", marginTop: 2 }}>portfolio average · derived from invoices · read-only</div>
              </div>
              <span className="num" data-testid="portfolio-price" style={{ fontSize: 15, fontWeight: 700, color: "var(--ui-text-mid)" }}>${(data?.portfolioSellPricePerKg ?? 0).toFixed(2)}</span>
            </div>
          </Card>

          <Card style={{ padding: 26 }}>
            <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Ownership scoring</div>
            <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 12 }}>owned machines are opportunities, not leaks</div>
            {[["rented", "Rented", "indigo"], ["financed", "Financed", "indigo"], ["owned", "Owned outright", "good"]].map(([key, label, dot], i) => {
              const o = s.ownershipScoring?.[key] || { enabled: true, description: "" };
              return (
                <div key={key} className="flex items-center" style={{ gap: 12, padding: "16px 0", borderTop: i === 0 ? "none" : "1px solid var(--ui-line-soft)" }}>
                  <span style={{ width: 8, height: 8, borderRadius: 9999, background: dotColor(dot) }} />
                  <div style={{ flex: 1 }}>
                    <div className="prose-i" style={{ fontSize: 14, fontWeight: 600, color: "var(--ui-text-hi)" }}>{label}</div>
                    <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 2 }}>{o.description}</div>
                  </div>
                  <Toggle on={o.enabled} onChange={(v) => setS((p) => ({ ...p, ownershipScoring: { ...p.ownershipScoring, [key]: { ...o, enabled: v } } }))} />
                </div>
              );
            })}
          </Card>

          <Card style={{ padding: 26 }}>
            <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Alerts</div>
            <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3, marginBottom: 12 }}>who hears about it</div>
            <div className="flex items-center" style={{ gap: 12, paddingTop: 4 }}>
              <div style={{ flex: 1 }}>
                <div className="prose-i" style={{ fontSize: 14, fontWeight: 600, color: "var(--ui-text-hi)" }}>Monday morning digest</div>
                <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 2 }}>{s.alerts?.recipient} · {s.alerts?.note}</div>
              </div>
              <Toggle on={s.alerts?.digestEnabled} onChange={(v) => setS((p) => ({ ...p, alerts: { ...p.alerts, digestEnabled: v } }))} />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
